/** @format */

import { createFeatureSelector, createSelector } from '@ngrx/store';

import { ProductSelectionState } from '@pdp/data-access/interfaces';
import { Warranty } from '@shared/data-access/interfaces';
import { selectAllProductSkus, selectResolvedSku } from '../product-selection.selectors';
import { WarrantyButtonState } from '@pdp/data-access/enums';

export const selectState = createFeatureSelector<ProductSelectionState>('productSelection');

export const selectWarrantiesState = createSelector(selectState, (state) => state?.warranties);

export const selectSelectedWarranty = createSelector(
	selectWarrantiesState,
	(state) => state?.selectedWarranty
);

export const selectWarrantyCallState = createSelector(
	selectWarrantiesState,
	(state) => state?.warrantyCallState
);

export const selectAllWarranties = createSelector(
	selectWarrantiesState,
	(state) => state?.allWarranties
);

export const selectSelectedWarrantyPlanSku = createSelector(
	selectSelectedWarranty,
	(selectedWarranty) => selectedWarranty?.planSku
);

export const selectWarrantiesForResolvedSku = createSelector(
	selectAllWarranties,
	selectResolvedSku,
	(allWarranties, resolvedSku): Warranty[] => {
		if (!resolvedSku || !allWarranties) {
			return [];
		}
		return allWarranties[resolvedSku.partNumber] ?? [];
	}
);

export const selectAemContentIdentifier = createSelector(
	selectWarrantiesForResolvedSku,
	(warrantiesForResolvedSku): string | undefined => {
		return warrantiesForResolvedSku?.[0]?.aemContentIdentifier;
	}
);

export const selectSelectedWarrantyDetails = createSelector(
	selectSelectedWarranty,
	selectWarrantiesForResolvedSku,
	(selectedWarranty, warranties) => {
		if (!selectedWarranty || !warranties?.length) {
			return undefined;
		}
		return warranties.find((w) => w.planSku === selectedWarranty.planSku);
	}
);

export const selectSelectedWarrantyDisplayName = createSelector(
	selectSelectedWarrantyDetails,
	(selectedWarrantyDetails) => selectedWarrantyDetails?.planDisplayName
);

export const selectIsProductWarrantyEligible = createSelector(
	selectAllProductSkus,
	(allSkus) => !!allSkus?.some((sku) => !!sku?.warrantyEligible)
);

export const selectIsResolvedSkuWarrantyEligible = createSelector(
	selectResolvedSku,
	(resolvedSku) => !!resolvedSku?.warrantyEligible
);

export const selectWarrantyButtonState = createSelector(
	selectResolvedSku,
	selectIsResolvedSkuWarrantyEligible,
	selectSelectedWarranty,
	(resolvedSku, isResolvedSkuWarrantyEligible, selectedWarranty) => {
		if (!resolvedSku) {
			return WarrantyButtonState.BeforeSkuResolve;
		}

		if (!isResolvedSkuWarrantyEligible) {
			return WarrantyButtonState.WarrantyNotAvailable;
		}

		if (selectedWarranty) {
			return WarrantyButtonState.WarrantySelected;
		}

		return WarrantyButtonState.WarrantyAvailable;
	}
);
